import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { uploadToBunny } from "@/lib/bunnyUpload";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Upload, Film, Image as ImageIcon, Info, AlertTriangle, Scale, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { FILMMAKER_TERMS_VERSION } from "@/lib/terms";

import GenreFields from "@/components/GenreFields";

// Price tiers mirror the server. ``premiering_only`` tiers unlock only
// when the filmmaker toggles the Premiering flag.
const PRICE_TIERS = [
  { amount: 100, premiering_only: false },
  { amount: 200, premiering_only: false },
  { amount: 300, premiering_only: false },
  { amount: 500, premiering_only: true },
  { amount: 1000, premiering_only: true },
];

// Default USD tiers paired to each Naira tier. Must mirror
// ``NAIRA_TO_USD_DEFAULTS_CENTS`` on the server exactly or filmmakers
// will see one preview and get another server-side.
const USD_DEFAULTS_CENTS = {
  100:  { african: 25,  diaspora: 100 },
  200:  { african: 50,  diaspora: 200 },
  300:  { african: 75,  diaspora: 300 },
  500:  { african: 125, diaspora: 500 },
  1000: { african: 250, diaspora: 1000 },
};
const usdFromCents = (c) => `$${(Number(c) / 100).toFixed(2)}`;

const VIDEO_MAX_MB = 500; // 500 MB upper bound
const POSTER_MAX_MB = 8;
const TRAILER_MAX_MB = 100;
const ACCEPTED_VIDEO_EXT = [".mp4", ".m4v"];
const ACCEPTED_POSTER_EXT = [".jpg", ".jpeg", ".png", ".webp"];
const ACCEPTED_TRAILER_EXT = [".mp4", ".m4v"];

function bytesToMB(b) { return (b / (1024 * 1024)).toFixed(1); }

export default function UploadFilm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "",
    synopsis: "",
    genre: "Drama",
    sub_genre: "",
    duration_minutes: 10,
    price_naira: 200,
    african_price_usd_cents: 50,
    diaspora_price_usd_cents: 200,
    premiering: false,
    themes: "",
    language: "English",
    country: "Nigeria",
    year: new Date().getFullYear(),
    phone_number: "",
    director: "",
    producer: "",
    cast: "",
    instagram_handle: "",
    twitter_handle: "",
  });
  const [video, setVideo] = useState(null);
  const [poster, setPoster] = useState(null);
  const [trailer, setTrailer] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [finalizing, setFinalizing] = useState(false);

  // Pre-fill the phone field from the filmmaker's saved profile so returning
  const isAdmin = user?.role === "admin";
  const [adminFilmmakers, setAdminFilmmakers] = useState([]);
  const [selectedFilmmakerId, setSelectedFilmmakerId] = useState("");

  // For admin uploads, fetch the list of filmmakers so the admin can attribute
  // the upload to one of them.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/filmmakers")
      .then((r) => setAdminFilmmakers(r.data || []))
      .catch(() => {});
  }, [isAdmin]);

  // When the admin picks a filmmaker, prefill the phone field from that
  // filmmaker's saved profile (falls back to the form's existing value).
  useEffect(() => {
    if (!isAdmin || !selectedFilmmakerId) return;
    const fm = adminFilmmakers.find((x) => x.id === selectedFilmmakerId);
    if (fm?.phone_number) {
      // eslint-disable-next-line
      setForm((p) => ({ ...p, phone_number: fm.phone_number }));
    }
  }, [selectedFilmmakerId, adminFilmmakers, isAdmin]);

  // Pre-fill the phone field from the filmmaker's saved profile so returning
  // filmmakers don't have to re-type it on every upload. They can still edit it.
  useEffect(() => {
    if (user?.phone_number && !isAdmin) {
      // eslint-disable-next-line
      setForm((p) => (p.phone_number ? p : { ...p, phone_number: user.phone_number }));
    }
  }, [user?.phone_number, isAdmin]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pickVideo = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    const okExt = ACCEPTED_VIDEO_EXT.some((ext) => name.endsWith(ext));
    if (!okExt) {
      toast.error(`Only MP4 (H.264) is supported. Please re-encode using HandBrake's "Fast 1080p30" preset.`);
      return;
    }
    if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
      toast.error(`Video is ${bytesToMB(file.size)} MB — maximum allowed is ${VIDEO_MAX_MB} MB.`);
      return;
    }
    setVideo(file);
  };

  const pickPoster = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    const okExt = ACCEPTED_POSTER_EXT.some((ext) => name.endsWith(ext));
    if (!okExt) {
      toast.error("Poster must be JPG, PNG, or WebP.");
      return;
    }
    if (file.size > POSTER_MAX_MB * 1024 * 1024) {
      toast.error(`Poster is ${bytesToMB(file.size)} MB — maximum allowed is ${POSTER_MAX_MB} MB.`);
      return;
    }
    setPoster(file);
  };

  const pickTrailer = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    const okExt = ACCEPTED_TRAILER_EXT.some((ext) => name.endsWith(ext));
    if (!okExt) {
      toast.error(`Trailer must be MP4 (H.264). Re-encode using HandBrake if needed.`);
      return;
    }
    if (file.size > TRAILER_MAX_MB * 1024 * 1024) {
      toast.error(`Trailer is ${bytesToMB(file.size)} MB — maximum allowed is ${TRAILER_MAX_MB} MB.`);
      return;
    }
    setTrailer(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isAdmin && !selectedFilmmakerId) {
      toast.error("Pick the filmmaker this upload is on behalf of.");
      return;
    }
    if (!video || !poster || !trailer) {
      toast.error("Please attach the video, poster, and trailer files.");
      return;
    }
    if (!form.phone_number.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }
    if (![100, 200, 300, 500, 1000].includes(form.price_naira)) {
      toast.error("Please pick one of the listed price tiers.");
      return;
    }
    if ((form.price_naira === 500 || form.price_naira === 1000) && !form.premiering) {
      toast.error("₦500 and ₦1000 are only available for Premiering films. Toggle Premiering on first.");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please read and accept the Filmmaker Terms before submitting.");
      return;
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("poster", poster);
    fd.append("bunny_upload", "true");
    fd.append("terms_accepted", "true");
    fd.append("terms_version", FILMMAKER_TERMS_VERSION);
    if (isAdmin && selectedFilmmakerId) {
      fd.append("filmmaker_id", selectedFilmmakerId);
    }

    setLoading(true);
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalBytes(0);
    setFinalizing(false);
    try {
      // 1) Details + poster to Cinevoya, 2) trailer + film straight to Bunny Stream (resumable).
      const { data: created } = await api.post("/films/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const total = trailer.size + video.size;
      setTotalBytes(total);
      const report = (base) => (sent) => {
        const done = base + sent;
        setUploadedBytes(done);
        setUploadProgress(Math.min(100, Math.round((done * 100) / total)));
      };
      await uploadToBunny(created.id, "trailer", trailer, report(0));
      await uploadToBunny(created.id, "video", video, report(trailer.size));
      setFinalizing(true);
      toast.success("Film submitted! It's now encoding on Bunny Stream; our team will review shortly.");
      navigate(isAdmin ? "/admin" : "/filmmaker");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setLoading(false);
      setFinalizing(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-4xl mx-auto pb-20">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Filmmaker</p>
          <h1 className="font-display text-4xl font-black">Submit a short film</h1>
          <p className="text-foreground/60 mt-2">Our team reviews submissions to keep the catalogue curated. Approved films go live for pay-per-view.</p>
        </div>

        <form onSubmit={submit} className="space-y-6 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="upload-film-form">
          {isAdmin && (
            <div className="rounded-lg border border-terracotta/40 bg-terracotta/8 p-4" data-testid="admin-attribution-block">
              <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1.5">
                Admin upload · attributing to a filmmaker
              </p>
              <Label className="text-foreground/80">Filmmaker</Label>
              <Select value={selectedFilmmakerId} onValueChange={setSelectedFilmmakerId}>
                <SelectTrigger className="mt-1.5 bg-ink border-white/15 h-11" data-testid="admin-filmmaker-picker">
                  <SelectValue placeholder="Pick the filmmaker this film belongs to…" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 max-h-72">
                  {adminFilmmakers.map((fm) => (
                    <SelectItem key={fm.id} value={fm.id}>
                      {fm.name} <span className="text-foreground/50">· {fm.email}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-[11px] text-foreground/55 leading-relaxed">
                Earnings will flow to the selected filmmaker. The film appears in their studio. This action is audit-logged.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="mt-1.5 bg-ink border-white/10 h-11" data-testid="upload-title-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Synopsis</Label>
              <Textarea value={form.synopsis} onChange={(e) => set("synopsis", e.target.value)} required rows={4} className="mt-1.5 bg-ink border-white/10" data-testid="upload-synopsis-input" />
            </div>
            <GenreFields genre={form.genre} subGenre={form.sub_genre} onChange={set} testPrefix="upload" />
            <div>
              <Label>Duration (minutes)</Label>
              <Input type="number" min={1} max={45} value={form.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} required className="mt-1.5 bg-ink border-white/10 h-11" data-testid="upload-duration-input" />
            </div>
            <div>
              <Label>Language</Label>
              <Input value={form.language} onChange={(e) => set("language", e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" />
            </div>
            <div>
              <Label>Year</Label>
              <Input type="number" min={1980} max={new Date().getFullYear()} value={form.year} onChange={(e) => set("year", Number(e.target.value))} className="mt-1.5 bg-ink border-white/10 h-11" />
            </div>
            <div className="sm:col-span-2">
              <Label>Themes (comma-separated)</Label>
              <Input value={form.themes} onChange={(e) => set("themes", e.target.value)} placeholder="identity, migration, family" className="mt-1.5 bg-ink border-white/10 h-11" />
            </div>
            <div className="sm:col-span-2 pt-3 mt-1 border-t border-white/5">
              <p className="text-[11px] uppercase tracking-widest text-foreground/45 font-semibold mb-3">Credits &amp; socials</p>
            </div>
            <div>
              <Label>Director</Label>
              <Input
                value={form.director}
                onChange={(e) => set("director", e.target.value)}
                placeholder="e.g. Tunde Adebayo"
                className="mt-1.5 bg-ink border-white/10 h-11"
                data-testid="upload-director-input"
              />
            </div>
            <div>
              <Label>Producer</Label>
              <Input
                value={form.producer}
                onChange={(e) => set("producer", e.target.value)}
                placeholder="e.g. Amina Bello"
                className="mt-1.5 bg-ink border-white/10 h-11"
                data-testid="upload-producer-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Cast</Label>
              <Input
                value={form.cast}
                onChange={(e) => set("cast", e.target.value)}
                placeholder="Lead names, comma-separated"
                className="mt-1.5 bg-ink border-white/10 h-11"
                data-testid="upload-cast-input"
              />
            </div>
            <div>
              <Label>Instagram <span className="text-foreground/40 font-normal">(optional)</span></Label>
              <div className="mt-1.5 flex items-center rounded-md bg-ink border border-white/10 h-11 overflow-hidden focus-within:border-terracotta/60">
                <span className="px-3 text-sm text-foreground/45 select-none">@</span>
                <Input
                  value={form.instagram_handle}
                  onChange={(e) => set("instagram_handle", e.target.value)}
                  placeholder="your_handle"
                  className="flex-1 bg-transparent border-0 h-11 px-0 focus-visible:ring-0"
                  data-testid="upload-instagram-input"
                />
              </div>
            </div>
            <div>
              <Label>Twitter / X <span className="text-foreground/40 font-normal">(optional)</span></Label>
              <div className="mt-1.5 flex items-center rounded-md bg-ink border border-white/10 h-11 overflow-hidden focus-within:border-terracotta/60">
                <span className="px-3 text-sm text-foreground/45 select-none">@</span>
                <Input
                  value={form.twitter_handle}
                  onChange={(e) => set("twitter_handle", e.target.value)}
                  placeholder="your_handle"
                  className="flex-1 bg-transparent border-0 h-11 px-0 focus-visible:ring-0"
                  data-testid="upload-twitter-input"
                />
              </div>
            </div>
            <div className="sm:col-span-2 pt-3 mt-1 border-t border-white/5"></div>
            <div className="sm:col-span-2">
              <Label>Phone number</Label>
              <Input
                type="tel"
                value={form.phone_number}
                onChange={(e) => set("phone_number", e.target.value)}
                placeholder="+234 800 123 4567 or +1 415 555 0123"
                required
                className="mt-1.5 bg-ink border-white/10 h-11"
                data-testid="upload-phone-input"
              />
              <p className="mt-1.5 text-[11px] text-foreground/55 leading-relaxed">
                Required. Include your country code (e.g. +234, +1, +44). Admin-only — we use this to reach you about payouts or curation notes. Never shown to the public.
              </p>
            </div>
            <div className="sm:col-span-2 rounded-xl border border-white/10 bg-ink/40 p-4 space-y-4">
              {/* Premiering toggle — gates the ₦500/₦1000 tiers and
                  promotes the film to the landing-page Premiering strip. */}
              <label className="flex items-start gap-3 cursor-pointer" data-testid="upload-premiering-row">
                <Checkbox
                  checked={form.premiering}
                  onCheckedChange={(v) => {
                    const next = Boolean(v);
                    // If turning premiering off while a premium price is
                    // selected, auto-drop back to ₦300 so the form stays valid.
                    setForm((p) => {
                      const needsDrop = !next && (p.price_naira === 500 || p.price_naira === 1000);
                      const nextNaira = needsDrop ? 300 : p.price_naira;
                      const defaults = USD_DEFAULTS_CENTS[nextNaira] || USD_DEFAULTS_CENTS[200];
                      return {
                        ...p,
                        premiering: next,
                        price_naira: nextNaira,
                        african_price_usd_cents: needsDrop ? defaults.african : p.african_price_usd_cents,
                        diaspora_price_usd_cents: needsDrop ? defaults.diaspora : p.diaspora_price_usd_cents,
                      };
                    });
                  }}
                  className="mt-0.5 border-gold data-[state=checked]:bg-gold data-[state=checked]:text-ink"
                  data-testid="upload-premiering-toggle"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    <Sparkles className="w-3.5 h-3.5 text-gold" /> Mark this as a Premiering title
                  </span>
                  <span className="block text-[11px] text-foreground/55 leading-relaxed mt-0.5">
                    Appears in the Premiering row on the landing page and unlocks ₦500 / ₦1000 pricing.
                  </span>
                </span>
              </label>

              <div>
                <Label>Pricing tier</Label>
                <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid="upload-price-tiers">
                  {PRICE_TIERS.map((t) => {
                    const locked = t.premiering_only && !form.premiering;
                    const active = form.price_naira === t.amount;
                    return (
                      <button
                        type="button"
                        key={t.amount}
                        disabled={locked}
                        onClick={() => {
                          // Picking a tier re-syncs both USD defaults so
                          // the three prices stay visually consistent.
                          const d = USD_DEFAULTS_CENTS[t.amount];
                          setForm((p) => ({
                            ...p,
                            price_naira: t.amount,
                            african_price_usd_cents: d.african,
                            diaspora_price_usd_cents: d.diaspora,
                          }));
                        }}
                        className={[
                          "h-10 rounded-md border text-xs font-semibold transition-all",
                          active ? "bg-gold text-ink border-gold" : "bg-ink border-white/10 text-foreground/80 hover:border-gold/50",
                          locked ? "opacity-40 cursor-not-allowed hover:border-white/10" : "",
                        ].join(" ")}
                        aria-label={`Set tier ₦${t.amount}${locked ? " (premiering only)" : ""}`}
                        data-testid={`upload-price-${t.amount}`}
                      >
                        ₦{t.amount}
                        {t.premiering_only && (
                          <Sparkles className="inline w-2.5 h-2.5 ml-0.5 -mt-0.5 text-gold" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {!form.premiering && (
                  <p className="mt-2 text-[11px] text-foreground/50">
                    ₦500 and ₦1000 unlock when Premiering is on.
                  </p>
                )}
              </div>

              {/* Per-region USD overrides. Pre-filled from the chosen
                  Naira tier so filmmakers who only care about NGN get
                  reasonable international pricing automatically. */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-white/5">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-foreground/55">Local (Nigeria)</Label>
                  <div className="mt-1.5 h-10 rounded-md bg-ink/60 border border-white/10 flex items-center px-3 text-gold font-display font-bold text-sm" data-testid="upload-price-local">
                    ₦{form.price_naira}
                  </div>
                  <p className="text-[10px] text-foreground/40 mt-1">From tier above</p>
                </div>
                <div>
                  <Label htmlFor="upload-african-usd" className="text-[11px] uppercase tracking-wider text-foreground/55">
                    African <span className="text-foreground/35 normal-case">(rest of Africa)</span>
                  </Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
                    <input
                      id="upload-african-usd"
                      type="number"
                      min="0.01"
                      max="1000"
                      step="0.01"
                      value={(form.african_price_usd_cents / 100).toFixed(2)}
                      onChange={(e) => {
                        const cents = Math.max(1, Math.round(parseFloat(e.target.value || "0") * 100));
                        set("african_price_usd_cents", cents);
                      }}
                      className="h-10 w-full rounded-md bg-ink border border-white/10 pl-6 pr-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/40"
                      data-testid="upload-african-usd-input"
                    />
                  </div>
                  <p className="text-[10px] text-foreground/40 mt-1">Default: {usdFromCents((USD_DEFAULTS_CENTS[form.price_naira] || USD_DEFAULTS_CENTS[200]).african)}</p>
                </div>
                <div>
                  <Label htmlFor="upload-diaspora-usd" className="text-[11px] uppercase tracking-wider text-foreground/55">
                    Diaspora <span className="text-foreground/35 normal-case">(US / UK / EU)</span>
                  </Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
                    <input
                      id="upload-diaspora-usd"
                      type="number"
                      min="0.01"
                      max="1000"
                      step="0.01"
                      value={(form.diaspora_price_usd_cents / 100).toFixed(2)}
                      onChange={(e) => {
                        const cents = Math.max(1, Math.round(parseFloat(e.target.value || "0") * 100));
                        set("diaspora_price_usd_cents", cents);
                      }}
                      className="h-10 w-full rounded-md bg-ink border border-white/10 pl-6 pr-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/40"
                      data-testid="upload-diaspora-usd-input"
                    />
                  </div>
                  <p className="text-[10px] text-foreground/40 mt-1">Default: {usdFromCents((USD_DEFAULTS_CENTS[form.price_naira] || USD_DEFAULTS_CENTS[200]).diaspora)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Video file</Label>
              <label className="mt-1.5 flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-white/10 hover:border-terracotta cursor-pointer bg-ink transition-colors" data-testid="upload-video-zone">
                <Film className="w-6 h-6 text-foreground/40 mb-1" />
                <span className="text-xs text-foreground/60 px-3 text-center">{video ? `${video.name} (${bytesToMB(video.size)} MB)` : "Click to choose video"}</span>
                <input type="file" accept=".mp4,.m4v,video/mp4" hidden onChange={(e) => pickVideo(e.target.files[0])} required />
              </label>
              <div className="mt-2 text-[11px] text-foreground/55 leading-relaxed flex gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-terracotta" />
                <span>
                  <span className="text-foreground/75 font-medium">MP4 (H.264) only</span> ·
                  <span className="text-foreground/85 font-semibold"> max {VIDEO_MAX_MB} MB</span> ·
                  720p–1080p · 24–30 fps · stereo AAC audio.
                  <span className="block mt-0.5 text-foreground/45">
                    Files larger than {VIDEO_MAX_MB} MB will be rejected — please compress in HandBrake before uploading.
                  </span>
                </span>
              </div>
            </div>
            <div>
              <Label>Trailer file</Label>
              <label className="mt-1.5 flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-white/10 hover:border-gold cursor-pointer bg-ink transition-colors" data-testid="upload-trailer-zone">
                <Film className="w-6 h-6 text-gold/70 mb-1" />
                <span className="text-xs text-foreground/60 px-3 text-center">{trailer ? `${trailer.name} (${bytesToMB(trailer.size)} MB)` : "Click to choose trailer"}</span>
                <input type="file" accept=".mp4,.m4v,video/mp4" hidden onChange={(e) => pickTrailer(e.target.files[0])} required data-testid="upload-trailer-input" />
              </label>
              <div className="mt-2 text-[11px] text-foreground/55 leading-relaxed flex gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-gold" />
                <span>
                  <span className="text-foreground/75 font-medium">MP4 (H.264) marketing clip</span> ·
                  <span className="text-foreground/85 font-semibold"> max {TRAILER_MAX_MB} MB</span> ·
                  typically 30–90 seconds. Shown publicly to drive rentals.
                </span>
              </div>
            </div>
            <div>
              <Label>Poster image</Label>
              <label className="mt-1.5 flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-white/10 hover:border-terracotta cursor-pointer bg-ink transition-colors" data-testid="upload-poster-zone">
                <ImageIcon className="w-6 h-6 text-foreground/40 mb-1" />
                <span className="text-xs text-foreground/60 px-3 text-center">{poster ? `${poster.name} (${bytesToMB(poster.size)} MB)` : "Click to choose poster"}</span>
                <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" hidden onChange={(e) => pickPoster(e.target.files[0])} required />
              </label>
              <div className="mt-2 text-[11px] text-foreground/55 leading-relaxed flex gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-terracotta" />
                <span>
                  <span className="text-foreground/75 font-medium">JPG, PNG or WebP</span> · max {POSTER_MAX_MB} MB ·
                  portrait <span className="text-foreground/75">2:3</span> ratio (e.g. 1000×1500 px) · sRGB colour.
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-terracotta/40 bg-terracotta/8 p-4 flex gap-3" data-testid="upload-size-notice">
            <Info className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
            <div className="text-xs text-foreground/80 leading-relaxed">
              <p className="font-semibold text-terracotta mb-1">Maximum video file size: {VIDEO_MAX_MB} MB</p>
              Uploads above {VIDEO_MAX_MB} MB will be rejected. Most short films (under 15 minutes at 1080p H.264) fit comfortably under this limit.
              If your file is larger, run it through <a href="https://handbrake.fr" target="_blank" rel="noreferrer" className="text-gold underline hover:text-terracotta">HandBrake</a> with the
              <em>&ldquo;Fast 1080p30&rdquo;</em> preset and lower the bitrate (e.g. 2,500 kb/s) — quality stays great and size drops dramatically.
            </div>
          </div>

          <div className="rounded-lg border border-gold/25 bg-gold/5 p-4 flex gap-3" data-testid="upload-codec-warning">
            <AlertTriangle className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <div className="text-xs text-foreground/75 leading-relaxed">
              <p className="font-medium text-gold mb-1">Recording on iPhone or Android?</p>
              Most phones default to <span className="font-mono">HEVC / H.265</span> which Chrome and
              Firefox cannot play. Convert to <span className="font-mono">H.264</span> before
              uploading using <a href="https://handbrake.fr" target="_blank" rel="noreferrer" className="text-gold underline hover:text-terracotta">HandBrake</a> →
              choose the <em>&ldquo;Fast 1080p30&rdquo;</em> or <em>&ldquo;Web Optimized&rdquo;</em> preset, then export as MP4.
              Uploads in unsupported codecs will be rejected automatically.
            </div>
          </div>

          <div
            className={`rounded-lg border p-4 transition-colors ${
              termsAccepted
                ? "border-emerald-500/30 bg-emerald-500/5"
                : "border-white/10 bg-ink/40"
            }`}
            data-testid="upload-terms-block"
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-terms"
                checked={termsAccepted}
                onCheckedChange={(v) => setTermsAccepted(!!v)}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-terracotta data-[state=checked]:border-terracotta"
                data-testid="upload-terms-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="accept-terms" className="font-medium text-foreground/95 cursor-pointer flex items-center gap-1.5">
                  <Scale className="w-4 h-4 text-gold" />
                  I have read and accept the Filmmaker Terms
                </Label>
                <p className="text-xs text-foreground/60 leading-relaxed mt-1.5">
                  By ticking this box you confirm you own the rights to this Content, you grant Cinevoya the licence set out in the
                  {" "}
                  <Link
                    to="/terms/filmmaker"
                    target="_blank"
                    rel="noopener"
                    className="text-terracotta underline underline-offset-2 hover:text-ember"
                    data-testid="upload-terms-link"
                  >
                    Filmmaker Terms &amp; Conditions (v{FILMMAKER_TERMS_VERSION})
                  </Link>, and you agree these terms are governed by Nigerian law.
                </p>
              </div>
            </div>
          </div>

          {loading && (
            <div
              className="rounded-lg border border-terracotta/40 bg-terracotta/8 p-4"
              data-testid="upload-progress-block"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground/90">
                  {finalizing ? (
                    <Loader2 className="w-4 h-4 text-gold animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 text-terracotta" />
                  )}
                  <span data-testid="upload-progress-label">
                    {finalizing ? "Finalising on server…" : "Uploading video"}
                  </span>
                </div>
                <span
                  className="font-display text-lg font-bold text-gold tabular-nums"
                  data-testid="upload-progress-percent"
                >
                  {uploadProgress}%
                </span>
              </div>
              <Progress value={uploadProgress} className="h-2 bg-ink" data-testid="upload-progress-bar" />
              <div className="mt-2 flex justify-between text-[11px] text-foreground/55 tabular-nums">
                <span data-testid="upload-progress-bytes">
                  {totalBytes > 0
                    ? `${bytesToMB(uploadedBytes)} / ${bytesToMB(totalBytes)} MB`
                    : "Preparing upload…"}
                </span>
                <span className="text-foreground/45">
                  {finalizing
                    ? "Hang tight — saving to storage"
                    : "Please keep this tab open"}
                </span>
              </div>
            </div>
          )}

          <Button type="submit" disabled={loading || !termsAccepted} className="w-full bg-terracotta hover:bg-ember text-white h-12" data-testid="upload-submit-btn">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {finalizing ? "Finalising…" : `Uploading ${uploadProgress}%`}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Submit for review
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
